import * as THREE from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

import DATA from './data';
import {
    calculateCameraPosition,
    makeTextureTemplateUrls,
    getInitialCameraPosition,
    getInitialOrbitTarget
} from './lib/util';


// settings for customization during local development
const DEBUG_MODE = false;
const VIEW_MODE = "FPV";
const initialNavPoint = DATA.navPoints[DATA.initialNavPoint];

// Canvas
const canvas = document.querySelector('canvas.webgl');

// camera
const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.02, 10000);
// Set the initial camera position to the initial navigation point's position
camera.position.set(initialNavPoint.position.x, initialNavPoint.position.y, initialNavPoint.position.z);

// scene
const scene = new THREE.Scene();

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
})
renderer.gammaFactor = 2.2;
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Using 2 as the max value for DPR
renderer.toneMapping = THREE.LinearToneMapping
// renderer.toneMappingExposure = 2;


/** 
 * Post
 */
const renderScene = new RenderPass(scene, camera);
const BLOOM_SCENE = 1;

const bloomLayer = new THREE.Layers();
bloomLayer.set(BLOOM_SCENE);
camera.layers.enable(BLOOM_SCENE);

const bloomPass = new UnrealBloomPass(new THREE.Vector2(sizes.width * 2, sizes.height * 2), 1.5, 0.4, 0.85);
bloomPass.threshold = 0.3
bloomPass.strength = 0.4
bloomPass.radius = 0.4

const outputPass = new OutputPass();

const bloomComposer = new EffectComposer(renderer);
bloomComposer.renderToScreen = false;
bloomComposer.addPass(renderScene);
bloomComposer.addPass(bloomPass);
bloomComposer.setSize(sizes.width, sizes.height);

const mixPass = new ShaderPass(
    new THREE.ShaderMaterial({
        uniforms: {
            baseTexture: { value: null },
            bloomTexture: { value: bloomComposer.renderTarget2.texture }
        },
        vertexShader: document.getElementById('vertexshader').textContent,
        fragmentShader: document.getElementById('fragmentshader').textContent,
        defines: {}
    }), 'baseTexture'
);
mixPass.needsSwap = true;


const finalComposer = new EffectComposer(renderer);
finalComposer.addPass(renderScene);
finalComposer.addPass(mixPass);
finalComposer.addPass(outputPass);
finalComposer.setSize(sizes.width, sizes.height);


// time
const clock = new THREE.Clock();



const initialStore = {
    app: null,
    camera,
    scene,
    canvas,
    sizes,
    renderer,
    bloomComposer,
    finalComposer,
    bloomLayer,
    clock,
    viewMode: VIEW_MODE,
    styleMode: "",
    cursor: { position: new THREE.Vector3(), rotation: new THREE.Quaternion() },
    cursorOpacity: 0,
    cameraPosition: getInitialCameraPosition(),
    lastMouseMove: Date.now(),
    zoomLvl: 0,
    fullScreenMode: false,
    debugMode: DEBUG_MODE,
    appLoaded: false,
    sceneInited: false,
    fov: 80,
    currentNavPoint: initialNavPoint,
    outgoingNavPoint: initialNavPoint,
    isNavigating: false,
    savedRotation: null,
    lerping: false,
    lerpValue: 0,
    minimapCamera: null,
    minimapRenderer: null,
    orbitControlsTarget: getInitialOrbitTarget(),
    sceneGraph: null,
    textures: {},
    materialCache: {},
    tourGuidedMode: true,
    tourModelActiveIdx: 0,
    tourPointActiveIdx: 0,
    tourLightMode: false,
    floorMarkers: [],
};


// Store.js
class Store {
    constructor() {
        this.store = initialStore;
        this.listeners = [];
    }

    getState() {
        return this.store;
    }

    setState(newStore) {
        this.store = { ...this.store, ...newStore };
        this.notifyAll();
    }

    listen(listener) {
        this.listeners.push(listener);
    }

    notifyAll() {
        this.listeners.forEach(listener => listener(this.store));
    }
}

const store = new Store();

export default store;